import { ScrollView, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { LoanAddressHeader } from "@/screens/loans/components/LoanAddressHeader"
import FullScreenBackground from "@/components/backgrounds/FullScreenBackgroundGradient"
import TextTabs, { TextTab } from "@/components/tabs/TextTabs"
import { BFBackButton } from "@/components/standard/BFBackButton"
import { RehabSOWHeadlineSummary } from "@/screens/loans/components/HeadlineSummarySection"
import { RehabBudgetTab } from "@/screens/rehab/RehabBudgetTab"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import {
  CONTINGENCY_CATEGORY,
  getRehabCategories,
} from "@/screens/rehab/utils/Rehab"
import RehabCategoryModal from "@/screens/rehab/RehabCategoryModal"
import { RehabDetailsTab } from "@/screens/rehab/RehabDetailsTab"
import { RehabSummaryTab } from "@/screens/rehab/RehabSummaryTab"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useViewedCallback } from "@/analytics/react"
import * as RehabEvents from "@/analytics/events/rehab"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"
import { type RehabBudgetType } from "@/generated/graphql"
import FullScreenLoader from "@/components/loading/FullScreenLoader"

export type RehabSowTabs = "budget" | "details" | "summary"

export default function RehabSowScreen() {
  const { loanApplication } = useLoanContext()
  const isDesktop = useIsDesktop()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const [selectedTab, setSelectedTab] = useState<RehabSowTabs>("budget")
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const [snapPoints, setSnapPoints] = useState(["65%"])

  const insets = useSafeAreaInsets()

  // "get-or-create" rehab budget query by passing loanApplication.id
  const {
    isDetailsTabDisabled,
    isSummaryTabDisabled,
    isLoading: isRehabLoading,
    error: rehabError,
    rehabBudgetId,
    rehabBudget,
  } = useRehabBudgetingFeature(loanApplication, isDesktop)

  useViewedCallback(() => {
    if (loanApplication?.property?.fullAddress) {
      RehabEvents.rehabSOWViewed({
        propertyAddress: loanApplication.property.fullAddress,
      })
    }
  }, [loanApplication?.property?.fullAddress])

  // Disable the Summary tab if totalBudget === 0 or GC info is missing.
  const TABS: Array<{ value: RehabSowTabs; label: string; disabled: boolean }> =
    [
      { value: "budget", label: "Budget", disabled: false },
      { value: "details", label: "Details", disabled: isDetailsTabDisabled },
      {
        value: "summary",
        label: "Summary",
        disabled: isSummaryTabDisabled,
      },
    ]

  const openModal = (category: string) => {
    setSelectedCategory(category)
    bottomSheetModalRef.current?.present()
  }

  const rehabCategories = useMemo(
    () => getRehabCategories(isDesktop),
    [isDesktop]
  )

  const categoryData = selectedCategory
    ? Object.values(rehabCategories).find(
        (def) => def.fieldName === selectedCategory
      ) || CONTINGENCY_CATEGORY.Contingency
    : null

  useEffect(() => {
    setSnapPoints(
      categoryData?.tallModal
        ? [isDesktop ? "65%" : "91%"]
        : isKeyboardOpen
          ? ["91%"]
          : ["65%"]
    )
  }, [selectedCategory, isKeyboardOpen])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        pressBehavior="close"
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  if (!rehabBudgetId || isRehabLoading) {
    return (
      <BFScreen dark={false} headerSticky={true}>
        <FullScreenLoader dark={false} />
      </BFScreen>
    )
  }
  if (rehabError) {
    return (
      <View>
        <BFBackButton />
        {/* Error UI */}
      </View>
    )
  }

  const tabClass = "mt-5"

  return (
    <BFScreen dark={true} headerSticky={true}>
      <FullScreenBackground />
      <View
        style={{ paddingTop: 12 + insets.top }}
        className={"px-5 pb-1 gap-3"}
      >
        <View className={"relative"}>
          <LoanAddressHeader />
          <BFBackButton className={"absolute top-1"} />
        </View>
        <TextTabs>
          {TABS.map((t) => (
            <TextTab
              dark={true}
              size={"sm"}
              key={t.value}
              label={t.label}
              disabled={t.disabled}
              value={t.value}
              selected={t.value === selectedTab}
              onSelect={() => {
                if (t.value === selectedTab) return
                setSelectedTab(t.value)

                // Track tab selection
                RehabEvents.rehabSOWTabSelected({
                  propertyAddress: loanApplication.property.fullAddress,
                  selectedTab: t.value,
                })
              }}
            />
          ))}
        </TextTabs>
      </View>
      <ScrollView
        key={selectedTab}
        showsVerticalScrollIndicator={false}
        className="flex-1 px-6 py-4 pb-1 my-0 bg-off-white"
        contentContainerStyle={{
          paddingBottom: insets.bottom,
        }}
      >
        <RehabSOWHeadlineSummary openModal={openModal} />

        {selectedTab === "budget" && (
          <View className={tabClass}>
            <RehabBudgetTab
              setSelectedTab={setSelectedTab}
              selectedCategory={selectedCategory}
              setSelectedCategory={(category) => {
                setSelectedCategory(category)
                openModal(category)
              }}
            />
          </View>
        )}
        {selectedTab === "details" && (
          <View className={tabClass}>
            <RehabDetailsTab
              setSelectedTab={setSelectedTab}
              rehabBudgetId={rehabBudgetId}
            />
          </View>
        )}
        {selectedTab === "summary" && (
          <View className={tabClass}>
            <RehabSummaryTab setSelectedTab={setSelectedTab} />
          </View>
        )}
      </ScrollView>
      {/* BottomSheet Modal */}
      <BottomSheetModal
        ref={bottomSheetModalRef}
        backdropComponent={renderBackdrop}
        detached={false}
        backgroundStyle={{
          backgroundColor: BFColors.offWhite,
          maxWidth: isDesktop ? 800 : undefined,
          marginHorizontal: isDesktop ? "auto" : undefined,
          marginBottom: insets.bottom,
        }}
        handleIndicatorStyle={{ backgroundColor: BFColors.offWhite }}
        enableDynamicSizing={false}
        snapPoints={snapPoints}
        onDismiss={() => {
          setSelectedCategory("")
          setSnapPoints(["65%"])
        }}
      >
        <BottomSheetView
          className={"flex-1 mx-3"}
          style={{
            minWidth: isDesktop ? 800 : undefined,
            paddingHorizontal: isDesktop ? 20 : undefined,
            marginHorizontal: isDesktop ? "auto" : undefined,
          }}
        >
          {categoryData && rehabBudget ? (
            <RehabCategoryModal
              loanApplication={loanApplication}
              rehabBudget={rehabBudget as RehabBudgetType}
              categoryData={categoryData}
              onKeyboardToggle={setIsKeyboardOpen}
              onDismiss={() => {
                bottomSheetModalRef.current?.dismiss()
              }}
              propertyAddress={loanApplication.property.fullAddress}
              contingencyType={
                categoryData.fieldName === "contingency"
                  ? {
                      value: rehabBudget.contingencyValue || "0",
                      amount: rehabBudget.contingencyAmount || "0",
                    }
                  : undefined
              }
            />
          ) : (
            <View />
          )}
        </BottomSheetView>
      </BottomSheetModal>
    </BFScreen>
  )
}
